import {
  API_ROUTES,
  MULTIPART_FIELDS,
  type ApiErrorBody,
  type CreateSubmissionResponse,
  type FieldIssue,
  type ManagementSession,
  type ManagementUser,
  type Paginated,
  type SubmissionDetail,
  type SubmissionStats,
  type SubmissionSummary,
} from '@mapeamento/shared';

/** Falha de chamada à API, já com a mensagem pronta para o usuário. */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details: FieldIssue[] = [],
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** Envia o formulário (JSON + anexos) em uma única requisição multipart. */
export async function submitMapping(
  payload: unknown,
  files: readonly File[],
  fetchImpl: typeof fetch = fetch,
): Promise<CreateSubmissionResponse> {
  const form = new FormData();
  form.append(MULTIPART_FIELDS.payload, JSON.stringify(payload));
  for (const file of files) form.append(MULTIPART_FIELDS.files, file, file.name);

  let response: Response;
  try {
    response = await fetchImpl(API_ROUTES.submissions, { method: 'POST', body: form });
  } catch {
    throw new ApiRequestError(
      'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente — seu rascunho continua salvo.',
      0,
    );
  }

  if (response.ok) return (await response.json()) as CreateSubmissionResponse;

  const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
  throw new ApiRequestError(
    body?.error.message ?? `Falha no envio (HTTP ${response.status}). Tente novamente.`,
    response.status,
    body?.error.details,
  );
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.ok) return (await response.json()) as T;
  const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
  throw new ApiRequestError(
    body?.error.message ?? `Falha na requisição (HTTP ${response.status}).`,
    response.status,
    body?.error.details,
  );
}

function authHeaders(token: string, json = false): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    ...(json ? { 'content-type': 'application/json' } : {}),
  };
}

export async function loginManagement(
  email: string,
  password: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ManagementSession> {
  const response = await fetchImpl(API_ROUTES.managementLogin, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return parseResponse<ManagementSession>(response);
}

export async function listSubmissions(
  token: string,
  params: { q?: string; page?: number; pageSize?: number },
  fetchImpl: typeof fetch = fetch,
): Promise<Paginated<SubmissionSummary>> {
  const query = new URLSearchParams({
    page: String(params.page ?? 1),
    pageSize: String(params.pageSize ?? 20),
  });
  if (params.q) query.set('q', params.q);
  const response = await fetchImpl(`${API_ROUTES.submissions}?${query}`, {
    headers: authHeaders(token),
  });
  return parseResponse<Paginated<SubmissionSummary>>(response);
}

export async function getSubmissionStats(
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SubmissionStats> {
  const response = await fetchImpl(API_ROUTES.submissionsStats, { headers: authHeaders(token) });
  return parseResponse<SubmissionStats>(response);
}

export async function getSubmissionDetail(
  token: string,
  id: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SubmissionDetail> {
  const response = await fetchImpl(API_ROUTES.submissionById(id), { headers: authHeaders(token) });
  return parseResponse<SubmissionDetail>(response);
}

export async function listManagementUsers(
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ManagementUser[]> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetchImpl(API_ROUTES.managementUsers, {
      headers: authHeaders(token),
      signal: controller.signal,
    });
    return parseResponse<ManagementUser[]>(response);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiRequestError('Não foi possível carregar os acessos agora.', 0);
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function createManagementUser(
  token: string,
  input: { nome: string; email: string; password: string },
  fetchImpl: typeof fetch = fetch,
): Promise<ManagementUser> {
  const response = await fetchImpl(API_ROUTES.managementUsers, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });
  return parseResponse<ManagementUser>(response);
}

export async function updateManagementUser(
  token: string,
  id: string,
  input: Partial<{ nome: string; email: string; password: string }>,
  fetchImpl: typeof fetch = fetch,
): Promise<ManagementUser> {
  const response = await fetchImpl(API_ROUTES.managementUserById(id), {
    method: 'PATCH',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });
  return parseResponse<ManagementUser>(response);
}

export async function deleteManagementUser(
  token: string,
  id: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const response = await fetchImpl(API_ROUTES.managementUserById(id), {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!response.ok) await parseResponse(response);
}

export async function downloadSubmissionsCsv(token: string): Promise<void> {
  const response = await fetch(API_ROUTES.submissionsExportCsv, { headers: authHeaders(token) });
  if (!response.ok) await parseResponse(response);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `mapeamento-fontes-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
